import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const MobileMessagesView = ({ conversations, setConversations, user, adminUsers, API_BASE }) => {
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (activeConversation) {
      fetchMessages(activeConversation.id);
    }
  }, [activeConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(
        `${API_BASE}/messenger/conversations/${conversationId}/messages`,
        { withCredentials: true }
      );
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !activeConversation) return;

    try {
      const response = await axios.post(
        `${API_BASE}/messenger/conversations/${activeConversation.id}/messages`,
        { content: newMessage },
        { withCredentials: true }
      );
      setMessages([...messages, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleStartConversation = async (recipientId) => {
    try {
      const response = await axios.post(
        `${API_BASE}/messenger/conversations`,
        { recipient_id: recipientId },
        { withCredentials: true }
      );
      setConversations([response.data, ...conversations]);
      setActiveConversation(response.data);
      setShowNewChat(false);
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getOtherUser = (conversation) => {
    if (!conversation || !conversation.participants) return { name: 'Unknown' };
    const other = conversation.participants.find(p => p.id !== user?.id);
    if (!other) return { name: 'Unknown' };
    // Handle various possible name properties
    const name = other.display_name || other.name || other.username || other.firstName || 'Unknown';
    return { ...other, name };
  };

  const getUserDisplayName = (admin) => {
    if (!admin) return 'Unknown';
    return admin.display_name || admin.name || admin.username ||
           (admin.firstName ? `${admin.firstName} ${admin.lastName || ''}`.trim() : null) ||
           `User ${admin.id}`;
  };

  // Conversation List View
  if (!activeConversation) {
    return (
      <div className="mobile-messages-view">
        {/* Header */}
        <div className="mobile-module-header">
          <div className="module-header-left">
            <div className="module-icon messages">💬</div>
            <span className="module-title">Messages</span>
          </div>
          <button className="add-btn" onClick={() => setShowNewChat(true)}>+</button>
        </div>

        {/* Conversations List */}
        <div className="conversations-list">
          {(!conversations || conversations.length === 0) ? (
            <div className="empty-state">
              <div className="empty-state-icon">💬</div>
              <div className="empty-state-title">No conversations</div>
              <div className="empty-state-text">Start a new chat to begin messaging</div>
              <button className="empty-state-btn" onClick={() => setShowNewChat(true)}>
                Start New Chat
              </button>
            </div>
          ) : (
            (conversations || []).map(conv => {
              const otherUser = getOtherUser(conv);
              return (
                <div
                  key={conv.id}
                  className={`conversation-item ${conv.unread_count > 0 ? 'unread' : ''}`}
                  onClick={() => setActiveConversation(conv)}
                >
                  <div className="conversation-avatar">
                    {otherUser.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="conversation-content">
                    <div className="conversation-header">
                      <span className="conversation-name">{otherUser.name}</span>
                      <span className="conversation-time">
                        {conv.last_message_at ? formatTime(conv.last_message_at) : ''}
                      </span>
                    </div>
                    <div className="conversation-preview">
                      {conv.last_message?.content || conv.last_message_content || 'No messages yet'}
                    </div>
                  </div>
                  {conv.unread_count > 0 && (
                    <div className="conversation-badge">{conv.unread_count}</div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* New Chat Modal */}
        {showNewChat && (
          <div className="modal-overlay" onClick={() => setShowNewChat(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>New Message</h3>
                <button className="modal-close" onClick={() => setShowNewChat(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="user-select-list">
                  {(adminUsers || []).filter(a => a.id !== user?.id).map(admin => (
                    <div
                      key={admin.id}
                      className="user-select-item"
                      onClick={() => handleStartConversation(admin.id)}
                    >
                      <div className="user-avatar">
                        {getUserDisplayName(admin).charAt(0).toUpperCase()}
                      </div>
                      <span className="user-name">{getUserDisplayName(admin)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Chat View
  const otherUser = getOtherUser(activeConversation);

  return (
    <div className="mobile-chat-view">
      {/* Chat Header */}
      <div className="chat-header">
        <button className="back-btn" onClick={() => setActiveConversation(null)}>
          ‹
        </button>
        <div className="chat-header-info">
          <div className="chat-avatar">
            {otherUser.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <span className="chat-name">{otherUser.name}</span>
        </div>
      </div>

      {/* Messages */}
      <div className="chat-messages">
        {messages.map((msg, index) => (
          <div
            key={msg.id || index}
            className={`chat-message ${msg.sender_id === user?.id ? 'sent' : 'received'}`}
          >
            <div className="message-bubble">
              {msg.content}
            </div>
            <div className="message-time">
              {formatTime(msg.created_at)}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="chat-input-container">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
        />
        <button
          className="send-btn"
          onClick={handleSendMessage}
          disabled={!newMessage.trim()}
        >
          ➤
        </button>
      </div>
    </div>
  );
};

export default MobileMessagesView;
